import { useState } from 'react';
import axios from 'axios';
import { Download, FileText, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { getSupportTicketAttachment } from '@/api/control/control-plane/control-plane';
import type { SupportAttachmentDto } from '@/api/control/missionImpactAPI.schemas';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useError } from '@/contexts/ErrorContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillSupportTemplate, formatBytes, formatSupportDate, getDateLocale, getSupportPersonLabel } from '@/lib/support';

interface SupportAttachmentListProps {
  orgId: string;
  ticketId: string;
  attachments: SupportAttachmentDto[];
}

/**
 * Saves a blob to disk under the given file name.
 */
function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * A ticket's attachments, each with its size, uploader and upload time: downloadable when clean. Only files that can't be downloaded yet (being
 * scanned) or at all (failed the virus check) are marked; clean files carry no badge.
 */
export function SupportAttachmentList({
  orgId,
  ticketId,
  attachments,
}: SupportAttachmentListProps) {
  const { t, language } = useLanguage();
  const locale = getDateLocale(language);
  const { handleApiError } = useError();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  /**
   * Downloads an attachment. A 409 means the scan status changed since the page loaded.
   */
  const handleDownload = async (attachment: SupportAttachmentDto) => {
    if (!attachment.id) return;
    setDownloadingId(attachment.id);
    try {
      const response = await getSupportTicketAttachment(orgId, ticketId, attachment.id);
      saveBlob(response.data, attachment.fileName ?? 'attachment');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        toast.error(t.support.detail.scanningTooltip);
      } else {
        handleApiError(
          error,
          { context: 'SupportAttachmentList.handleDownload' },
          { showToast: false }
        );
        toast.error(t.support.detail.downloadFailed);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  if (attachments.length === 0) {
    return <p className="text-sm text-muted-foreground">{t.support.detail.noAttachments}</p>;
  }

  return (
    <Table className="table-fixed">
      <TableBody>
        {attachments.map((attachment) => (
          <TableRow key={attachment.id}>
            <TableCell className="w-6 px-0 align-top">
              <FileText className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden />
            </TableCell>
            <TableCell className="whitespace-normal">
              <div className="truncate text-sm font-medium">{attachment.fileName}</div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span>{formatBytes(attachment.fileSize)}</span>
                <span aria-hidden>·</span>
                <span>
                  {fillSupportTemplate(t.support.detail.uploadedBy, {
                    name: getSupportPersonLabel(attachment.uploadedBy, t.support.person),
                  })}
                </span>
                {attachment.scanStatus === 'Pending' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium background-neutral text-neutral">
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        {t.support.detail.scanning}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{t.support.detail.scanningTooltip}</TooltipContent>
                  </Tooltip>
                )}
                {attachment.scanStatus === 'Unavailable' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 font-medium text-error">
                        <ShieldAlert className="h-3 w-3" aria-hidden />
                        {t.support.detail.unavailable}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{t.support.detail.unavailableTooltip}</TooltipContent>
                  </Tooltip>
                )}
              </div>
              {attachment.uploadedAt && (
                <div className="text-xs text-muted-foreground">{formatSupportDate(attachment.uploadedAt, locale)}</div>
              )}
            </TableCell>
            <TableCell className="w-10 px-0 text-right">
              {attachment.scanStatus === 'Clean' ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`${t.support.detail.download} ${attachment.fileName ?? ''}`}
                  disabled={downloadingId === attachment.id}
                  onClick={() => {
                    void handleDownload(attachment);
                  }}
                >
                  <Download className="h-4 w-4" />
                </Button>
              ) : (
                <span className="inline-block h-8 w-8" aria-hidden />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
